import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PageHeader } from '@/components/page-header';
import { Settings, Eye, EyeOff } from 'lucide-react';
import {
  ColumnConfiguratorDrawer,
  type ColumnDefinition,
  DEFAULT_COLUMNS,
} from '@/components/column-configurator-drawer';

// Sample project data for demonstration
const SAMPLE_PROJECTS = [
  {
    id: '1',
    projectName: 'Digital Transformation Initiative',
    clientName: 'Acme Corporation',
    projectManager: 'Sarah Johnson',
    budget: '£250,000',
    actualCost: '£180,000',
    revenue: '£220,000',
    profitMargin: '18%',
    billingStatus: 'Current',
    teamMembers: 8,
    allocatedHours: 2400,
    utilisationRate: '85%',
    projectStatus: 'In Progress',
    healthIndicator: 'Green',
    phase: 'Implementation',
    completionPercentage: '65%',
    startDate: '01/03/2024',
    endDate: '30/09/2024',
    duration: '7 months',
    daysRemaining: 45,
    clientContact: 'John Smith',
    industry: 'Technology',
    accountManager: 'Emma Wilson',
    milestonesCompleted: '5/8',
    tasksOverdue: 2,
    riskLevel: 'Low',
    qualityScore: '4.5/5',
  },
  {
    id: '2',
    projectName: 'Cloud Migration Project',
    clientName: 'TechStart Ltd',
    projectManager: 'Michael Chen',
    budget: '£180,000',
    actualCost: '£195,000',
    revenue: '£180,000',
    profitMargin: '-8%',
    billingStatus: 'Overdue',
    teamMembers: 6,
    allocatedHours: 1800,
    utilisationRate: '92%',
    projectStatus: 'In Progress',
    healthIndicator: 'Red',
    phase: 'Testing',
    completionPercentage: '78%',
    startDate: '15/01/2024',
    endDate: '15/06/2024',
    duration: '5 months',
    daysRemaining: -15,
    clientContact: 'Lisa Brown',
    industry: 'Financial Services',
    accountManager: 'David Lee',
    milestonesCompleted: '6/7',
    tasksOverdue: 8,
    riskLevel: 'High',
    qualityScore: '3.8/5',
  },
  {
    id: '3',
    projectName: 'ERP Implementation',
    clientName: 'Global Industries',
    projectManager: 'Rachel Green',
    budget: '£500,000',
    actualCost: '£420,000',
    revenue: '£480,000',
    profitMargin: '12%',
    billingStatus: 'Current',
    teamMembers: 12,
    allocatedHours: 4800,
    utilisationRate: '88%',
    projectStatus: 'In Progress',
    healthIndicator: 'Amber',
    phase: 'Configuration',
    completionPercentage: '45%',
    startDate: '01/02/2024',
    endDate: '31/12/2024',
    duration: '11 months',
    daysRemaining: 180,
    clientContact: 'Robert Taylor',
    industry: 'Manufacturing',
    accountManager: 'Sophie Martin',
    milestonesCompleted: '3/9',
    tasksOverdue: 4,
    riskLevel: 'Medium',
    qualityScore: '4.2/5',
  },
];

// Column ID to data key mapping
const COLUMN_DATA_MAP: Record<string, keyof typeof SAMPLE_PROJECTS[0]> = {
  'project-name': 'projectName',
  'client-name': 'clientName',
  'project-manager': 'projectManager',
  'budget': 'budget',
  'actual-cost': 'actualCost',
  'revenue': 'revenue',
  'profit-margin': 'profitMargin',
  'billing-status': 'billingStatus',
  'team-members': 'teamMembers',
  'allocated-hours': 'allocatedHours',
  'utilisation-rate': 'utilisationRate',
  'project-status': 'projectStatus',
  'health-indicator': 'healthIndicator',
  'phase': 'phase',
  'completion-percentage': 'completionPercentage',
  'start-date': 'startDate',
  'end-date': 'endDate',
  'duration': 'duration',
  'days-remaining': 'daysRemaining',
  'client-contact': 'clientContact',
  'industry': 'industry',
  'account-manager': 'accountManager',
  'milestones-completed': 'milestonesCompleted',
  'tasks-overdue': 'tasksOverdue',
  'risk-level': 'riskLevel',
  'quality-score': 'qualityScore',
};

export function ColumnConfiguratorDemo() {
  const [columns, setColumns] = useState<ColumnDefinition[]>(DEFAULT_COLUMNS);
  const [modalOpen, setModalOpen] = useState(false);

  // Get visible columns sorted by order
  const visibleColumns = columns
    .filter((col) => col.visible)
    .sort((a, b) => a.order - b.order);

  const handleSaveColumns = (newColumns: ColumnDefinition[]) => {
    setColumns(newColumns);
  };

  const getHealthBadgeVariant = (health: string) => {
    switch (health.toLowerCase()) {
      case 'green':
        return 'default';
      case 'amber':
        return 'secondary';
      case 'red':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status.toLowerCase()) {
      case 'in progress':
        return 'default';
      case 'completed':
        return 'default';
      case 'on hold':
        return 'secondary';
      case 'not started':
        return 'outline';
      default:
        return 'outline';
    }
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <PageHeader
        title="Column Configurator Demo"
        description="Professional Services Automation - Project List View"
      />

      {/* Info Card */}
      <Card>
        <CardHeader>
          <CardTitle>Column Configuration</CardTitle>
          <CardDescription>
            Click the "Configure Columns" button to customise which columns appear in the project list
            and control their display order. This demo showcases the column configurator modal designed
            for UK mid-market professional services firms.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <p className="text-sm font-medium">
                Visible Columns: {visibleColumns.length} of {columns.length}
              </p>
              <div className="flex flex-wrap gap-1">
                {visibleColumns.map((col) => (
                  <Badge key={col.id} variant="secondary" className="text-xs">
                    {col.name}
                  </Badge>
                ))}
              </div>
            </div>
            <Button onClick={() => setModalOpen(true)} className="gap-2">
              <Settings className="h-4 w-4" />
              Configure Columns
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Project List Table */}
      <Card>
        <CardHeader>
          <CardTitle>Project List</CardTitle>
          <CardDescription>
            Sample projects displaying configured columns
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  {visibleColumns.map((col) => (
                    <TableHead key={col.id} className="font-semibold">
                      {col.name}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {SAMPLE_PROJECTS.map((project) => (
                  <TableRow key={project.id}>
                    {visibleColumns.map((col) => {
                      const dataKey = COLUMN_DATA_MAP[col.id];
                      const value = project[dataKey];

                      // Special rendering for certain columns
                      if (col.id === 'health-indicator') {
                        return (
                          <TableCell key={col.id}>
                            <Badge variant={getHealthBadgeVariant(value as string)}>
                              {value}
                            </Badge>
                          </TableCell>
                        );
                      }

                      if (col.id === 'project-status') {
                        return (
                          <TableCell key={col.id}>
                            <Badge variant={getStatusBadgeVariant(value as string)}>
                              {value}
                            </Badge>
                          </TableCell>
                        );
                      }

                      if (col.id === 'profit-margin') {
                        const isNegative = (value as string).startsWith('-');
                        return (
                          <TableCell key={col.id}>
                            <span className={isNegative ? 'text-destructive font-medium' : 'text-success-600 font-medium'}>
                              {value}
                            </span>
                          </TableCell>
                        );
                      }

                      if (col.id === 'billing-status') {
                        const isOverdue = value === 'Overdue';
                        return (
                          <TableCell key={col.id}>
                            <Badge variant={isOverdue ? 'destructive' : 'default'}>
                              {value}
                            </Badge>
                          </TableCell>
                        );
                      }

                      if (col.id === 'risk-level') {
                        const riskVariant = 
                          value === 'High' ? 'destructive' :
                          value === 'Medium' ? 'secondary' :
                          'default';
                        return (
                          <TableCell key={col.id}>
                            <Badge variant={riskVariant}>
                              {value}
                            </Badge>
                          </TableCell>
                        );
                      }

                      if (col.id === 'tasks-overdue') {
                        const count = value as number;
                        return (
                          <TableCell key={col.id}>
                            {count > 0 ? (
                              <span className="text-destructive font-medium">{count}</span>
                            ) : (
                              <span className="text-muted-foreground">{count}</span>
                            )}
                          </TableCell>
                        );
                      }

                      return (
                        <TableCell key={col.id}>
                          {value?.toString() || '-'}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Feature List */}
      <Card>
        <CardHeader>
          <CardTitle>Key Features</CardTitle>
          <CardDescription>
            This column configurator includes all requested functionality
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <h4 className="font-medium flex items-center gap-2">
                <Eye className="h-4 w-4 text-primary" />
                Column Management
              </h4>
              <ul className="text-sm text-muted-foreground space-y-1 ml-6 list-disc">
                <li>Show/hide individual columns with visibility toggle</li>
                <li>Drag and drop to reorder columns</li>
                <li>Project Name column is locked and always visible</li>
                <li>Real-time search across column names and descriptions</li>
              </ul>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium flex items-center gap-2">
                <Settings className="h-4 w-4 text-primary" />
                Presets & Filters
              </h4>
              <ul className="text-sm text-muted-foreground space-y-1 ml-6 list-disc">
                <li>4 pre-configured view presets</li>
                <li>Category filtering (Financial, Resource, etc.)</li>
                <li>Combined search and category filtering</li>
                <li>Custom preset when manually modified</li>
              </ul>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium flex items-center gap-2">
                <EyeOff className="h-4 w-4 text-primary" />
                Bulk Actions
              </h4>
              <ul className="text-sm text-muted-foreground space-y-1 ml-6 list-disc">
                <li>Show All - makes all columns visible</li>
                <li>Hide All - hides all except locked columns</li>
                <li>Reset to Default - restores original configuration</li>
                <li>Column count display (visible/total)</li>
              </ul>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium flex items-center gap-2">
                <Badge className="h-4 w-4" />
                User Experience
              </h4>
              <ul className="text-sm text-muted-foreground space-y-1 ml-6 list-disc">
                <li>Unsaved changes warning dialog</li>
                <li>Validation prevents saving with no visible columns</li>
                <li>Loading states and success/error notifications</li>
                <li>Keyboard accessible (Tab, Enter, Escape)</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Column Configurator Drawer */}
      <ColumnConfiguratorDrawer
        open={modalOpen}
        onOpenChange={setModalOpen}
        columns={columns}
        onSave={handleSaveColumns}
      />
    </div>
  );
}
